import { AlertCircle } from "lucide-react";
import React, { useContext, useState, useEffect } from "react";
import { Drawer, Button, Switch, Alert } from "@mantine/core";
import { SegmentationBlocksContext } from "../../providers/SegmentationBlocksProvider";
import { ISegmentationBlock } from "../../@types/segmentationBlocks";
import { formatDateTimeISO } from "../../../../utils/DateTime";
import ConfirmationModal from "../../../../themes/components/ConfirmationModal";

interface EditSegmentationBlockDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  block: ISegmentationBlock | null;
}

const EditSegmentationBlockDrawer: React.FC<
  EditSegmentationBlockDrawerProps
> = ({ isOpen, onClose, block }) => {
  const context = useContext(SegmentationBlocksContext);
  const [isActive, setIsActive] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);

  useEffect(() => {
    if (block) {
      setIsActive(block.is_active);
    }
  }, [block]);

  const handleStatusChange = (checked: boolean): void => {
    setPendingStatus(checked);
    setShowConfirmModal(true);
  };

  const confirmStatusChange = async (): Promise<void> => {
    if (block && pendingStatus !== null && context) {
      try {
        await context.updateBlock(block.id, pendingStatus);
        setIsActive(pendingStatus);
      } catch {
        // Restore original state on error
        setIsActive(block.is_active);
      }
    }
    setShowConfirmModal(false);
    setPendingStatus(null);
  };

  const cancelStatusChange = (): void => {
    setShowConfirmModal(false);
    setPendingStatus(null);
  };

  const handleClose = (): void => {
    setShowConfirmModal(false);
    setPendingStatus(null);
    onClose();
  };

  if (!context || !block) return null;

  return (
    <>
      <Drawer
        opened={isOpen}
        onClose={handleClose}
        title={null}
        padding={0}
        size="xl"
        position="right"
        styles={{
          header: { display: "none" },
          drawer: { padding: 0 },
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-8"
            style={{ backgroundColor: "rgba(6, 33, 51, 0.05)" }}
          >
            <h2 className="text-[18px] font-bold text-[#062133] m-0">
              Edit Segmentation Block
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-[#062133] hover:opacity-70 bg-transparent border-none cursor-pointer p-0"
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                role="img"
                aria-labelledby="edit-segmentation-close-title"
              >
                <title id="edit-segmentation-close-title">Close</title>
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-6 p-6">
            {/* Title */}
            <div>
              <div className="font-medium text-[14px] text-[#062133] mb-1">
                Title
              </div>
              <div className="text-[14px] text-[#839099]">
                {block.config?.title || block.segments[0]?.name || "Untitled"}
              </div>
            </div>

            {/* Reason for block */}
            <div>
              <div className="font-medium text-[14px] text-[#062133] mb-1">
                Reason for block
              </div>
              <div className="text-[14px] text-[#839099]">
                {block.config?.reason || "No reason provided"}
              </div>
            </div>

            {/* Applied employee segments */}
            <div>
              <div className="font-medium text-[14px] text-[#062133] mb-1">
                Applied employee segments
              </div>
              <div className="text-[14px] text-[#839099]">
                {block.segments.map((segment, index) => (
                  <span key={segment.id}>
                    {segment.name}
                    {segment.matched_employee_count !== null && (
                      <span> ({segment.matched_employee_count})</span>
                    )}
                    {index < block.segments.length - 1 && ", "}
                  </span>
                ))}
              </div>
            </div>

            {/* Date created */}
            <div>
              <div className="font-medium text-[14px] text-[#062133] mb-1">
                Date created
              </div>
              <div className="text-[14px] text-[#839099]">
                {formatDateTimeISO(block.created_at)}
              </div>
            </div>

            {/* Created by (User) */}
            <div>
              <div className="font-medium text-[14px] text-[#062133] mb-1">
                Created by (User)
              </div>
              <div className="text-[14px] text-[#839099]">
                {block.created_by
                  ? `${block.created_by.first_name} ${block.created_by.last_name}`
                  : "Unknown"}
              </div>
            </div>

            {/* Estimated match alert */}
            <Alert
              icon={<AlertCircle size={16} />}
              color="orange"
              styles={{
                root: {
                  backgroundColor: "#FFF4EA",
                  borderRadius: "8px",
                  border: "none",
                },
                message: {
                  fontWeight: 500,
                  fontSize: "12px",
                  color: "#FF922B",
                },
                icon: {
                  color: "#FF922B",
                },
              }}
            >
              Estimated match: {block.total_matched_employee_count ?? 0}{" "}
              Employees
            </Alert>

            {/* Segment status toggle */}
            <div className="flex items-center justify-between gap-6">
              <div className="flex-1">
                <div className="font-medium text-[14px] text-[#062133] mb-1">
                  Segment status
                </div>
                <p className="text-[12px] text-[#839099] leading-4">
                  Activate segment by switching the toggle. Default is
                  deactivated.
                </p>
              </div>
              <Switch
                checked={isActive}
                onChange={(e) => handleStatusChange(e.currentTarget.checked)}
                size="md"
                color="dark"
                disabled={context.isUpdating}
              />
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 mt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                fullWidth
                styles={{
                  root: {
                    border: "1px solid #062133",
                    color: "#062133",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    height: "42px",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClose}
                fullWidth
                styles={{
                  root: {
                    backgroundColor: "#062133",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    height: "42px",
                    "&:hover": {
                      backgroundColor: "#0a3d52",
                    },
                  },
                }}
              >
                Save Segmentation Block
              </Button>
            </div>
          </div>
        </div>
      </Drawer>

      <ConfirmationModal
        opened={showConfirmModal}
        onClose={cancelStatusChange}
        onConfirm={confirmStatusChange}
        title={`${
          pendingStatus ? "Activate" : "Deactivate"
        } segmentation block`}
        description={`Are you sure you would like to ${
          pendingStatus ? "activate" : "deactivate"
        } this block?`}
        confirmLabel={pendingStatus ? "Activate" : "Deactivate"}
        loading={context.isUpdating}
      />
    </>
  );
};

export default EditSegmentationBlockDrawer;
